// ============================================================================
// MerchantCenterSyncTab — Google Merchant Center sync dashboard
//
// Consumes backend APIs:
//   GET    /google-merchant-sync/stats/
//   GET    /google-merchant-sync/logs/
//   POST   /google-merchant-sync/trigger/
//   POST   /google-merchant-sync/reconcile/
//
// Layout:
//  ┌─ Stats tiles (total / active / deleted / last sync / failures)
//  ├─ Toolbar (Sync All + Verify against Google)
//  ├─ Freshness line (last sync / last verified against Merchant Center)
//  ├─ Categories table (click Sync → opens sidebar with live logs)
// ============================================================================
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudDownloadOutlined,
  DatabaseOutlined,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Empty,
  message,
  Modal,
  Pagination,
  Spin,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getMerchantCenterSyncLogs,
  getMerchantCenterSyncStats,
  triggerMerchantCenterReconcile,
  triggerMerchantCenterSync,
} from "../../apis/merchant_center_sync.api";
import { usePermissions } from "../../custom_hooks/permission/permission_hook";
import type {
  ICategorySyncSummary,
  IMerchantCenterSyncLog,
  IMerchantCenterSyncStats,
} from "../../interfaces/merchant_center_sync.interface";
import styles from "./merchant_center_sync_tab.module.scss";

dayjs.extend(relativeTime);

// ──────────────────────────── helpers ────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const classMap: Record<string, string> = {
    completed: styles.statusCompleted,
    started: styles.statusStarted,
    failed: styles.statusFailed,
    thread_completed: styles.statusThreadCompleted,
  };
  const iconMap: Record<string, React.ReactNode> = {
    completed: <CheckCircleOutlined />,
    started: <SyncOutlined spin />,
    failed: <CloseCircleOutlined />,
    thread_completed: <CheckCircleOutlined />,
  };
  return (
    <span className={`${styles.statusBadge} ${classMap[status] ?? ""}`}>
      {iconMap[status]} {status === "thread_completed" ? "done" : status}
    </span>
  );
};

// ──────────────────────────── component ────────────────────────────────────

const MerchantCenterSyncTab: React.FC = () => {
  const { hasPermission } = usePermissions();
  // ─── state ───
  const [stats, setStats] = useState<IMerchantCenterSyncStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [triggerAllLoading, setTriggerAllLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  // drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerCategory, setDrawerCategory] =
    useState<ICategorySyncSummary | null>(null);
  const [drawerLogs, setDrawerLogs] = useState<IMerchantCenterSyncLog[]>([]);
  const [drawerLogsTotal, setDrawerLogsTotal] = useState(0);
  const [drawerLogsPage, setDrawerLogsPage] = useState(1);
  const [drawerLogsLoading, setDrawerLogsLoading] = useState(false);
  const [drawerSyncing, setDrawerSyncing] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCountRef = useRef(0);
  const MAX_POLLS = 60; // 10 minutes max (60 * 10s)

  const reconcilePollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconcilePollCountRef = useRef(0);
  const MAX_RECONCILE_POLLS = 30; // 15 minutes max (30 * 30s)

  // ─── fetchers ───
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const data = await getMerchantCenterSyncStats();
    if (data) setStats(data);
    setStatsLoading(false);
  }, []);

  const fetchDrawerLogs = useCallback(
    async (categoryId: number, page = 1) => {
      setDrawerLogsLoading(true);
      const data = await getMerchantCenterSyncLogs({
        category_id: categoryId,
        page,
        page_size: 15,
      });
      if (data) {
        setDrawerLogs(data.results);
        setDrawerLogsTotal(data.count);
      }
      setDrawerLogsLoading(false);
    },
    []
  );

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ─── polling ───
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    setDrawerSyncing(false);
  }, []);

  const startPolling = useCallback(
    (categoryId: number) => {
      stopPolling();
      setDrawerSyncing(true);
      pollCountRef.current = 0;
      pollRef.current = setInterval(async () => {
        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLLS) {
          stopPolling();
          message.warning("Sync is taking too long. Check back later.");
          return;
        }
        const data = await getMerchantCenterSyncLogs({
          category_id: categoryId,
          page: 1,
          page_size: 15,
        });
        if (data) {
          setDrawerLogs(data.results);
          setDrawerLogsTotal(data.count);
          const latest = data.results[0];
          if (
            latest &&
            (latest.status === "completed" ||
              latest.status === "failed" ||
              latest.status === "thread_completed")
          ) {
            stopPolling();
            fetchStats();
            if (latest.status === "failed") {
              message.error(
                `Sync failed for ${latest.category_name ?? "category"}`
              );
            } else {
              message.success(
                `Sync completed for ${latest.category_name ?? "category"}`
              );
            }
          }
        }
      }, 10_000);
    },
    [stopPolling, fetchStats]
  );

  const stopReconcilePolling = useCallback(() => {
    if (reconcilePollRef.current) {
      clearInterval(reconcilePollRef.current);
      reconcilePollRef.current = null;
    }
    setReconciling(false);
  }, []);

  // Reconcile walks every product in Merchant Center, so it finishes minutes
  // later — watch last_reconciled_at rather than the trigger response.
  const startReconcilePolling = useCallback(
    (previousReconciledAt: string | null) => {
      stopReconcilePolling();
      setReconciling(true);
      reconcilePollCountRef.current = 0;
      reconcilePollRef.current = setInterval(async () => {
        reconcilePollCountRef.current += 1;
        if (reconcilePollCountRef.current > MAX_RECONCILE_POLLS) {
          stopReconcilePolling();
          message.warning(
            "Verification is still running. Reload in a few minutes to see the result."
          );
          return;
        }
        // Bypasses fetchStats so the table does not flash its spinner every poll.
        const data = await getMerchantCenterSyncStats();
        if (data) setStats(data);
        if (data && data.last_reconciled_at !== previousReconciledAt) {
          stopReconcilePolling();
          if (data.last_reconcile_status === "failed") {
            message.error(
              "Verification against Merchant Center failed — counts left unchanged"
            );
          } else {
            message.success("Counts verified against Merchant Center");
          }
        }
      }, 30_000);
    },
    [stopReconcilePolling]
  );

  useEffect(() => {
    return () => {
      stopPolling();
      stopReconcilePolling();
    };
  }, [stopPolling, stopReconcilePolling]);

  // ─── handlers ───
  const openCategoryDrawer = (category: ICategorySyncSummary) => {
    setDrawerCategory(category);
    setDrawerOpen(true);
    setDrawerLogsPage(1);
    fetchDrawerLogs(category.category_id, 1);
  };

  const handleDrawerSync = async () => {
    if (!hasPermission("sync.trigger")) {
      message.error("You don't have permission to trigger a sync");
      return;
    }
    if (!drawerCategory) return;
    setDrawerSyncing(true);
    const res = await triggerMerchantCenterSync({
      category_id: drawerCategory.category_id,
    });
    if (res) {
      message.success(res.message);
      // Immediately refresh logs then start polling
      await fetchDrawerLogs(drawerCategory.category_id, 1);
      startPolling(drawerCategory.category_id);
    } else {
      setDrawerSyncing(false);
    }
  };

  const handleCloseDrawer = () => {
    stopPolling();
    setDrawerOpen(false);
    setDrawerCategory(null);
    setDrawerLogs([]);
    setDrawerLogsTotal(0);
  };

  const handleSyncAll = () => {
    if (!hasPermission("sync.trigger")) {
      message.error("You don't have permission to trigger a sync");
      return;
    }
    Modal.confirm({
      title: "Sync All Categories",
      content:
        "This reads Merchant Center back to correct the counts, then queues a full " +
        "sync for all active categories. It runs in the background and may take a while. Continue?",
      onOk: async () => {
        setTriggerAllLoading(true);
        const res = await triggerMerchantCenterSync();
        if (res) message.success(res.message);
        setTriggerAllLoading(false);
      },
    });
  };

  const handleReconcile = () => {
    if (!hasPermission("sync.trigger")) {
      message.error("You don't have permission to trigger a sync");
      return;
    }
    Modal.confirm({
      title: "Verify against Merchant Center",
      content:
        "This reads every product back from Google and corrects the counts below. " +
        "It takes a few minutes and does not change what is live in Merchant Center. Continue?",
      onOk: async () => {
        const previous = stats?.last_reconciled_at ?? null;
        setReconciling(true);
        const res = await triggerMerchantCenterReconcile();
        if (res) {
          message.success(res.message);
          startReconcilePolling(previous);
        } else {
          setReconciling(false);
        }
      },
    });
  };

  // ─── category table columns ───
  const categoryColumns: ColumnsType<ICategorySyncSummary> = [
    {
      title: "Category",
      dataIndex: "category_name",
      sorter: (a, b) => a.category_name.localeCompare(b.category_name),
      render: (val: string, record: ICategorySyncSummary) => (
        <span>
          {val}
          {!record.is_active && (
            <Tooltip title="Category is inactive, so the sync never visits it — its products stay in Merchant Center until it is reactivated or they are removed">
              <Tag color="default" style={{ marginLeft: 6, fontSize: 10 }}>
                inactive
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: "Active in DB",
      dataIndex: "db_variant_count",
      width: 140,
      sorter: (a, b) => (a.db_variant_count ?? 0) - (b.db_variant_count ?? 0),
      render: (val: number | undefined) => (
        <span style={{ fontWeight: 600 }}>{(val ?? 0).toLocaleString()}</span>
      ),
    },
    {
      title: "In GMC",
      dataIndex: "product_count",
      width: 120,
      sorter: (a, b) => (a.product_count ?? 0) - (b.product_count ?? 0),
      defaultSortOrder: "descend",
      render: (val: number | undefined, record: ICategorySyncSummary) => {
        const gmcCount = val ?? 0;
        const missing = record.missing_count ?? 0;
        const stale = record.stale_count ?? 0;
        return (
          <span>
            <Tooltip title="Live in Merchant Center and still eligible in the DB">
              <span style={{ fontWeight: 600 }}>
                {gmcCount.toLocaleString()}
              </span>
            </Tooltip>
            {missing > 0 && (
              <Tag color="warning" style={{ marginLeft: 6, fontSize: 10 }}>
                {missing.toLocaleString()} missing
              </Tag>
            )}
            {stale > 0 && (
              <Tooltip title="Still in Merchant Center but no longer eligible — the next sync deletes these">
                <Tag color="error" style={{ marginLeft: 4, fontSize: 10 }}>
                  {stale.toLocaleString()} stale
                </Tag>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: "Actions",
      width: 200,
      render: (_: any, record: ICategorySyncSummary) =>
        record.is_active ? (
          <Button
            size="small"
            type="primary"
            icon={<SyncOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              openCategoryDrawer(record);
            }}
          >
            Sync & View Logs
          </Button>
        ) : (
          <Tooltip title="Syncing an inactive category would re-push its products, not remove them. Reactivate the category first.">
            <Button size="small" disabled icon={<SyncOutlined />}>
              Sync & View Logs
            </Button>
          </Tooltip>
        ),
    },
  ];

  // ─── drawer log columns ───
  const drawerLogColumns: ColumnsType<IMerchantCenterSyncLog> = [
    {
      title: "Time",
      dataIndex: "created_at",
      width: 140,
      render: (val: string) => (
        <Tooltip title={dayjs(val).format("YYYY-MM-DD HH:mm:ss")}>
          {dayjs(val).fromNow()}
        </Tooltip>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 120,
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      title: "Details",
      dataIndex: "action_details",
      ellipsis: true,
      render: (val: Record<string, any> | string | null) => {
        if (!val) return "-";
        if (typeof val === "string") return val;
        const parts: string[] = [];
        if (val.item_count != null) parts.push(`${val.item_count} items`);
        if (val.chunk_count != null) parts.push(`chunk #${val.chunk_count}`);
        if (val.type === "single_item_failure")
          parts.push(`variant ${val.variant_id} failed`);
        if (val.error && parts.length === 0) parts.push(val.error);
        return parts.length > 0 ? parts.join(", ") : JSON.stringify(val);
      },
    },
  ];

  const reconcileSummary =
    stats?.reconcile && typeof stats.reconcile === "object"
      ? stats.reconcile
      : null;
  // a failed reconcile logs only an error, so it has no counts to report
  const reconcileFailed = stats?.last_reconcile_status === "failed";
  const reconcileCounts =
    !reconcileFailed && typeof reconcileSummary?.in_gmc === "number"
      ? reconcileSummary
      : null;

  // ─── render ───
  return (
    <Spin spinning={statsLoading && !stats}>
      <div className={styles.tabContainer}>
        {/* ──── Stats Tiles ──── */}
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statCardHead}>
              <span className={styles.statLabel}>Total Products</span>
              <span className={`${styles.statIcon} ${styles.statIconPrimary}`}>
                <DatabaseOutlined />
              </span>
            </div>
            <div className={styles.statValue}>
              {stats?.eligible_in_db?.toLocaleString() ?? "-"}
            </div>
            <div className={styles.statHint}>Meant to go to GMC</div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHead}>
              <span className={styles.statLabel}>Active</span>
              <span className={`${styles.statIcon} ${styles.statIconSuccess}`}>
                <CheckCircleOutlined />
              </span>
            </div>
            <div className={styles.statValue}>
              {stats?.active_products?.toLocaleString() ?? "-"}
            </div>
            <div className={styles.statHint}>
              {stats?.last_reconciled_at
                ? `Live in Merchant Center, verified ${dayjs(
                    stats.last_reconciled_at
                  ).fromNow()}`
                : "Accepted by the API — not yet verified against Google"}
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHead}>
              <span className={styles.statLabel}>Not in GMC</span>
              <span className={`${styles.statIcon} ${styles.statIconWarning}`}>
                <ExclamationCircleOutlined />
              </span>
            </div>
            <div className={styles.statValue}>
              {stats?.not_in_gmc?.toLocaleString() ?? "-"}
            </div>
            <div className={styles.statHint}>Eligible in DB, not in GMC</div>
          </div>

          <div className={styles.statCard}>
            <div className={styles.statCardHead}>
              <span className={styles.statLabel}>Stale in GMC</span>
              <span className={`${styles.statIcon} ${styles.statIconDanger}`}>
                <MinusCircleOutlined />
              </span>
            </div>
            <div className={styles.statValue}>
              {stats?.stale_in_gmc?.toLocaleString() ?? "-"}
            </div>
            <div className={styles.statHint}>Inactive/deleted, still in GMC</div>
          </div>
        </div>

        {/* ──── Toolbar ──── */}
        <div className={styles.toolbar}>
          <Button
            icon={<SyncOutlined />}
            loading={triggerAllLoading}
            onClick={handleSyncAll}
          >
            Sync All Categories
          </Button>
          <Button
            icon={<CloudDownloadOutlined />}
            loading={reconciling}
            onClick={handleReconcile}
          >
            {reconciling ? "Verifying..." : "Verify against Google"}
          </Button>
          <span className={styles.freshness}>
            Last sync{" "}
            {stats?.last_sync_at ? dayjs(stats.last_sync_at).fromNow() : "never"}
            {" · "}
            Counts verified{" "}
            {stats?.last_reconciled_at
              ? dayjs(stats.last_reconciled_at).fromNow()
              : "never"}
            {stats?.reconcile_is_stale && (
              <Tooltip title="Until a verification runs, these counts are what the API accepted, not what Merchant Center holds.">
                <Tag color="warning" style={{ marginLeft: 8, fontSize: 10 }}>
                  unverified
                </Tag>
              </Tooltip>
            )}
          </span>
        </div>
        {reconcileCounts && (
          <div className={styles.freshnessDetail}>
            Last verification: {reconcileCounts.in_gmc?.toLocaleString()} in
            Merchant Center
            {typeof reconcileCounts.marked_not_live === "number" &&
              ` · ${reconcileCounts.marked_not_live.toLocaleString()} corrected to not-live`}
            {typeof reconcileCounts.tracked_from_gmc === "number" &&
              ` · ${reconcileCounts.tracked_from_gmc.toLocaleString()} found live but untracked`}
            {!!reconcileCounts.orphans_in_gmc &&
              ` · ${reconcileCounts.orphans_in_gmc.toLocaleString()} orphans with no variant`}
          </div>
        )}
        {reconcileFailed && (
          <div className={styles.freshnessDetail}>
            Verification failed
            {stats?.last_reconcile_attempt_at
              ? ` ${dayjs(stats.last_reconcile_attempt_at).fromNow()}`
              : ""}
            {" — counts below are unverified."}
            {reconcileSummary?.error ? ` ${reconcileSummary.error}` : ""}
          </div>
        )}

        {/* ──── Categories Table ──── */}
        <div className={styles.tableCard}>
          <Table
            dataSource={(stats?.categories ?? []).filter(
              // inactive categories still holding GMC products need action, so keep them visible
              (c) => c.is_active || c.product_count > 0 || c.stale_count > 0
            )}
            columns={categoryColumns}
            rowKey="category_id"
            pagination={false}
            size="middle"
            loading={statsLoading}
            rowClassName={styles.tableRow}
            onRow={(record) => ({
              onClick: () => openCategoryDrawer(record),
              style: { cursor: "pointer" },
            })}
          />
        </div>

        {/* ──── Category Sync Drawer ──── */}
        <Drawer
          title={
            drawerCategory ? (
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>
                  {drawerCategory.category_name}
                </div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {(drawerCategory.db_variant_count ?? 0).toLocaleString()} active in DB
                  {" / "}
                  {(drawerCategory.product_count ?? 0).toLocaleString()} in GMC
                  {!!drawerCategory.missing_count &&
                    ` / ${drawerCategory.missing_count.toLocaleString()} missing`}
                  {!!drawerCategory.stale_count &&
                    ` / ${drawerCategory.stale_count.toLocaleString()} stale`}
                </div>
              </div>
            ) : (
              "Category Sync"
            )
          }
          open={drawerOpen}
          onClose={handleCloseDrawer}
          width={600}
        >
          {drawerCategory && (
            <div>
              {/* Sync trigger button */}
              <div style={{ marginBottom: 20 }}>
                <Tooltip
                  title={
                    drawerCategory.is_active
                      ? ""
                      : "Syncing an inactive category would re-push its products, not remove them. Reactivate the category first."
                  }
                >
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={drawerSyncing} />}
                    loading={drawerSyncing}
                    onClick={handleDrawerSync}
                    disabled={!drawerCategory.is_active}
                    block
                    size="large"
                  >
                    {drawerSyncing ? "Syncing..." : "Sync Now"}
                  </Button>
                </Tooltip>
                {drawerSyncing && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: "#64748b",
                      textAlign: "center",
                    }}
                  >
                    Auto-refreshing every 10s...
                  </div>
                )}
              </div>

              {/* Sync logs */}
              <div className={styles.drawerSectionTitle}>Sync Logs</div>
              <Spin spinning={drawerLogsLoading}>
                {drawerLogs.length === 0 ? (
                  <Empty
                    description="No sync logs for this category yet. Click Sync Now to start."
                    style={{ margin: "40px 0" }}
                  />
                ) : (
                  <>
                    <Table
                      dataSource={drawerLogs}
                      columns={drawerLogColumns}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      expandable={{
                        expandedRowRender: (record) => (
                          <div>
                            {record.action_details && (
                              <>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    marginBottom: 6,
                                  }}
                                >
                                  Action Details
                                </div>
                                <div className={styles.payloadBlock}>
                                  {typeof record.action_details === "string"
                                    ? record.action_details
                                    : JSON.stringify(
                                        record.action_details,
                                        null,
                                        2
                                      )}
                                </div>
                              </>
                            )}
                            {record.status_details && (
                              <>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#64748b",
                                    textTransform: "uppercase",
                                    marginTop: 12,
                                    marginBottom: 6,
                                  }}
                                >
                                  Status Details
                                </div>
                                <div className={styles.payloadBlock}>
                                  {record.status_details}
                                </div>
                              </>
                            )}
                          </div>
                        ),
                        rowExpandable: (record) =>
                          !!(record.action_details || record.status_details),
                      }}
                    />
                    {drawerLogsTotal > 15 && (
                      <div className={styles.paginationRow}>
                        <Pagination
                          current={drawerLogsPage}
                          pageSize={15}
                          total={drawerLogsTotal}
                          showSizeChanger={false}
                          size="small"
                          onChange={(page) => {
                            setDrawerLogsPage(page);
                            if (drawerCategory) {
                              fetchDrawerLogs(
                                drawerCategory.category_id,
                                page
                              );
                            }
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </Spin>
            </div>
          )}
        </Drawer>
      </div>
    </Spin>
  );
};

export default MerchantCenterSyncTab;
